import React, { useEffect, useState, useRef, useCallback } from 'react';
import { IconButton, InputArea } from '@wix/design-system';
import cs from 'classnames';

import s from './UserResponseInput.module.scss';
import { isTouchDevice } from '../../utils';
import { useIMEEnter } from './useIMEEnter';
import { useAutoHeight } from './useAutoHeight';

interface UserResponseInputProps {
  placeholder: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSend: (ctaType: 'buttonClick' | 'enterKeyPressed') => void;
  value: string;
  inputDisabled: boolean;
  sendButtonDisabled: boolean;
  onClick: () => void;
  onFocus: () => void;
}

export const UserResponseInput = ({
  placeholder,
  onChange,
  onSend,
  value,
  inputDisabled,
  sendButtonDisabled,
  onClick,
  onFocus,
}: UserResponseInputProps) => {
  const textareaRef = useRef<InputArea>(null);
  const [isFocused, setIsFocused] = useState(false);

  useIMEEnter(textareaRef);
  useAutoHeight(textareaRef, value);

  useEffect(() => {
    if (textareaRef.current && !isTouchDevice()) {
      textareaRef.current.focus();
    }
  }, [inputDisabled]);

  const handleFocus = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
    setIsFocused(true);
    onFocus();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  return (
    <div
      className={cs(s.inputWrapper, { [s.disabled]: inputDisabled })}
      onClick={handleFocus}
    >
      {inputDisabled && <div className={s.clickCatcher} onClick={onClick} />}
      <InputArea
        dataHook="chat-user-response-input"
        placeholder={placeholder}
        onChange={onChange}
        onEnterPressed={(event) => {
          if (
            (event as never as KeyboardEvent).isComposing ||
            event.shiftKey ||
            sendButtonDisabled
          ) {
            return;
          }
          onSend('enterKeyPressed');
          event.stopPropagation();
          event.preventDefault();
        }}
        value={value}
        disabled={inputDisabled}
        className={cs(s.input, {
          [s.inputFocused]: isFocused,
          [s.inputDisabled]: inputDisabled,
        })}
        rows={1}
        ref={textareaRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      <IconButton
        dataHook="chat-user-response-submit"
        aria-label="send"
        className={cs(s.button, { [s.empty]: sendButtonDisabled })}
        disabled={inputDisabled}
        onClick={() => {
          if (!sendButtonDisabled) {
            onSend('buttonClick');
          }
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.34315 0.278416L13.7071 6.32784C14.0976 6.69906 14.0976 7.30093 13.7071 7.67216L7.34315 13.7216C6.95262 14.0928 6.31946 14.0928 5.92893 13.7216C5.53841 13.3504 5.53841 12.7485 5.92893 12.3773L10.5858 8L6.99382e-07 8L5.24537e-07 6L10.5858 6L5.92893 1.62273C5.53841 1.25151 5.53841 0.649639 5.92893 0.278417C6.31946 -0.0928063 6.95262 -0.0928063 7.34315 0.278416Z"
          />
        </svg>
      </IconButton>
    </div>
  );
};
